import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Badge, Box, Button, Center, Flex, Icon, Input, Spinner, Table, Tbody, Td, Text, Th, Thead, Tfoot, Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  createColumnHelper, flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import { useKeycloak } from '@react-keycloak/web';
import { MdCheck } from 'react-icons/md';
import Card from 'components/card/Card';
import * as NotificationService from 'services/NotificationService';
import { AdminNotification } from 'utils/interfaces';
import { isAdmin } from 'utils/auth-utils';
import { formatDateTime } from 'utils/common-utils';

const PAGE_SIZE = 10;

export default function NotificationsPage() {
  const { keycloak } = useKeycloak();
  const history = useHistory();
  const jwtToken = keycloak?.token;
  const adminUser = isAdmin(keycloak!);

  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [inputPageNumber, setInputPageNumber] = useState('');

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)'
  );

  const loadNotifications = async (page = 0) => {
    if (!jwtToken) return;
    setIsLoading(true);
    const response = await NotificationService.getAllPaginated(jwtToken, () => keycloak?.logout(), page, PAGE_SIZE);
    if (response) {
      setNotifications(response.content);
      setPageNumber(page);
      setTotalPages(response.totalPages);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadNotifications(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken]);

  const handleMarkAsRead = async (id: number) => {
    if (!jwtToken) return;
    const ok = await NotificationService.markAsRead(jwtToken, () => keycloak?.logout(), id);
    if (ok) {
      setNotifications(prev => prev.map(n => (n.id === id ? { ...n, isRead: true } : n)));
    }
  };

  const handleRowClick = (notification: AdminNotification) => {
    if (!notification.isRead) handleMarkAsRead(notification.id);
    if (notification.interventionReportId) {
      history.push(`/admin/rapporti-intervento/${notification.interventionReportId}`);
    }
  };

  const handlePrevious = () => { if (pageNumber > 0) loadNotifications(pageNumber - 1); };
  const handleNext = () => { if (pageNumber < totalPages - 1) loadNotifications(pageNumber + 1); };

  const columnHelper = createColumnHelper<AdminNotification>();

  const columns = [
    columnHelper.accessor('message', {
      id: 'message',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '14px' }} textTransform="none" color="gray.400">
          Messaggio
        </Text>
      ),
      cell: (info: any) => (
        <Text color={textColor} fontSize="sm" fontWeight={info.row.original.isRead ? '400' : '700'} whiteSpace="normal">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '14px' }} textTransform="none" color="gray.400">
          Data
        </Text>
      ),
      cell: (info: any) => (
        <Text color={textColor} fontSize="sm" whiteSpace="nowrap">
          {formatDateTime(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('isRead', {
      id: 'isRead',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '14px' }} textTransform="none" color="gray.400">
          Stato
        </Text>
      ),
      cell: (info: any) => (
        <Flex justifyContent="flex-start">
          <Badge colorScheme={info.getValue() ? 'gray' : 'blue'} fontSize="12px" borderRadius="full" px="2">
            {info.getValue() ? 'Letta' : 'Non letta'}
          </Badge>
        </Flex>
      ),
    }),
    {
      id: 'actions',
      header: () => <Text fontSize={{ sm: '10px', lg: '14px' }} color="gray.400" />,
      cell: (info: any) => {
        const notification: AdminNotification = info.row.original;
        return !notification.isRead ? (
          <Button
            onClick={e => { e.stopPropagation(); handleMarkAsRead(notification.id); }}
            variant="ghost"
            size="xs"
            _hover={{ bg: 'gray.200' }}
            _focus={{ boxShadow: 'none' }}
          >
            <Icon as={MdCheck} w={4} h={4} />
          </Button>
        ) : null;
      },
    },
  ];

  const table = useReactTable({
    columns,
    data: notifications,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (!adminUser) {
    return (
      <Center minH="100vh">
        <Text fontSize="xl" color="red.500" textAlign="center">
          Solo gli amministratori possono accedere a questa sezione.
        </Text>
      </Center>
    );
  }

  return (
    <Box pt={{ base: '40px', md: '80px', xl: '78px' }} pb="40px" pr="40px" fontFamily="'DM Sans', system-ui, -apple-system, sans-serif">
      <Flex justifyContent="space-between" alignItems="center" mb="30px" flexWrap="wrap" gap="10px">
        <Text fontSize="2xl" fontWeight="bold" color="#9FB50C">
          Notifiche
        </Text>
      </Flex>

      <Card
        boxShadow={shadow}
        flexDirection="column"
        width={{ base: '100%', md: '96%' }}
        mx="10px"
        pt="25px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        <Box overflowX="auto">
          <Table variant="simple" color="gray.500" mb="24px" mt="12px" w="100%" size="sm">
            <Thead>
              {table.getHeaderGroups().map(headerGroup => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      pe="10px"
                      borderColor={borderColor}
                      paddingInline="0px"
                      maxW={header.column.id === 'message' ? '320px' : undefined}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, rowIndex) => (
                  <Tr key={`loading-row-${rowIndex}`}>
                    {columns.map((_, columnIndex) => (
                      <Td key={`loading-cell-${rowIndex}-${columnIndex}`} pb="6px" borderColor="transparent" textAlign="center">
                        {rowIndex === 0 ? <Spinner size="sm" color="gray.500" /> : null}
                      </Td>
                    ))}
                  </Tr>
                ))
              ) : notifications.length > 0 ? (
                table.getRowModel().rows.map(row => (
                  <Tr key={row.id} _hover={{ bg: 'gray.200' }} cursor="pointer" onClick={() => handleRowClick(row.original)}>
                    {row.getVisibleCells().map(cell => (
                      <Td
                        key={cell.id}
                        pb="2px"
                        paddingInline="0px"
                        fontSize={{ sm: '14px' }}
                        minW={{ sm: '150px', md: '200px', lg: cell.column.id === 'message' ? undefined : 'auto' }}
                        maxW={cell.column.id === 'message' ? '320px' : undefined}
                        borderColor="transparent"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    ))}
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={columns.length} textAlign="center">
                    Nessuna notifica
                  </Td>
                </Tr>
              )}
            </Tbody>
            <Tfoot w="100%">
              <Tr>
                <Td colSpan={columns.length} textAlign="center">
                  <Flex justifyContent="center" alignItems="center" mt={4}>
                    <Button onClick={handlePrevious} isDisabled={pageNumber === 0} height="100%">
                      Precedente
                    </Button>
                    <Text mx={2}>
                      Pagina {pageNumber + 1} di {Math.max(totalPages, 1)}
                    </Text>
                    <Button onClick={handleNext} isDisabled={pageNumber === totalPages - 1} height="100%">
                      Successiva
                    </Button>
                    <Flex alignItems="center" mx={2}>
                      <Input
                        type="number"
                        placeholder="Pagina"
                        value={inputPageNumber}
                        onChange={e => setInputPageNumber(e.target.value)}
                        width="90px"
                        mx={2}
                      />
                      <Button
                        onClick={() => {
                          const pageNum = parseInt(inputPageNumber, 10);
                          if (!isNaN(pageNum) && pageNum > 0 && pageNum <= totalPages) {
                            loadNotifications(pageNum - 1);
                            setInputPageNumber('');
                          }
                        }}
                      >
                        Vai
                      </Button>
                    </Flex>
                  </Flex>
                </Td>
              </Tr>
            </Tfoot>
          </Table>
        </Box>
      </Card>
    </Box>
  );
}
